/**
 * DualScorePanel — pupil readiness + instructor coaching score view.
 *
 * Feb 11, 2026. Renders the top-of-review summary panel for the new
 * dual-scoring system:
 *
 *   • Pupil Readiness  — overall %, readiness band, sub-domains
 *   • Instructor Coaching — /51, %, examiner band, 17-competency sheet
 *   • Evidence ledger — timestamped, click-to-seek, with fault tag
 *
 * Pure presentational — receives a validated `dualScore` payload
 * from the backend. Click on any evidence row's timestamp pill calls
 * `onSeek(seconds)` so the parent can scrub the stitched player.
 */
import React from "react";
import { GraduationCap, Award, Clock, AlertTriangle, Sparkles, Target } from "lucide-react";
import { resolveVerdict } from "../../utils/verdictLabels";

const BAND_LABELS = {
  test_ready: "Test ready",
  nearly_ready: "Nearly ready",
  needs_guided_development: "Needs guided development",
  not_safe_independently_yet: "Not safe independently yet",
  excellent: "Excellent",
  good: "Good",
  needs_improvement: "Needs improvement",
  risk_concern: "Risk concern",
};

const BAND_TONE = {
  // pupil
  test_ready: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  nearly_ready: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  needs_guided_development: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  not_safe_independently_yet: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  // instructor
  excellent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  good: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  needs_improvement: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  risk_concern: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const FAULT_TONE = {
  pupil: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  instructor: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  both: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function fmtTs(secs) {
  const s = Math.max(0, Number(secs) || 0);
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function PupilCard({ pupil, verdict }) {
  if (!pupil) return null;
  const band = pupil.readiness_band || "needs_guided_development";
  const subs = [
    { label: "Road reading", v: pupil.road_reading_pct },
    { label: "Vehicle control", v: pupil.vehicle_control_pct },
    { label: "Hazard awareness", v: pupil.hazard_awareness_pct },
    { label: "Decision making", v: pupil.decision_making_pct },
  ];
  // Phase D trust loop (Feb 13, 2026): the headline pill now shows
  // the backend's intent-locked `display_verdict` (e.g. likely_pass
  // for a mock_test) instead of the schema-bound readiness_band.
  // We fall through to band wording only when verdict is missing.
  const showVerdictPill = verdict && verdict.verdict;
  return (
    <div
      data-testid="dual-score-pupil"
      className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-950/40 to-slate-900 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/40 shrink-0">
          <GraduationCap className="w-5 h-5 text-indigo-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-indigo-300">
            Pupil readiness
          </p>
          <div className="flex items-baseline gap-3 mt-0.5">
            <p
              data-testid="pupil-overall-pct"
              className="text-3xl font-extrabold text-white"
            >
              {pupil.overall_pct ?? 0}%
            </p>
            <span
              data-testid="pupil-band"
              data-display-verdict={showVerdictPill ? verdict.verdict : undefined}
              className={`text-[10px] uppercase tracking-widest font-extrabold px-2 py-0.5 rounded-full border ${
                showVerdictPill ? verdict.toneClass : (BAND_TONE[band] || "")
              }`}
            >
              {showVerdictPill ? verdict.label : (BAND_LABELS[band] || band)}
            </span>
          </div>
          {pupil.key_reason && (
            <p
              data-testid="pupil-key-reason"
              className="text-[12px] text-slate-300 mt-1 leading-snug"
            >
              {pupil.key_reason}
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {subs.map((s) => (
          <div
            key={s.label}
            className="rounded-lg bg-slate-900/60 border border-slate-800 p-2"
          >
            <p className="text-[9px] uppercase tracking-widest text-slate-500 font-extrabold">
              {s.label}
            </p>
            <p className="text-base font-extrabold text-white">
              {s.v ?? 0}%
            </p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3 text-[10px] text-slate-400">
        <span>
          Independence:{" "}
          <span className="font-extrabold text-slate-200 capitalize">
            {pupil.independence_level || "—"}
          </span>
        </span>
        <span>·</span>
        <span>
          Instructor dependency:{" "}
          <span className="font-extrabold text-slate-200 capitalize">
            {pupil.instructor_dependency_level || "—"}
          </span>
        </span>
      </div>
    </div>
  );
}

function InstructorCard({ instructor }) {
  if (!instructor) return null;
  const band = instructor.band || "needs_improvement";
  // Group competencies by category so the UI mirrors the DVSA sheet.
  const grouped = (instructor.competencies || []).reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});
  return (
    <div
      data-testid="dual-score-instructor"
      className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-950/40 to-slate-900 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center border border-violet-500/40 shrink-0">
          <Award className="w-5 h-5 text-violet-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-violet-300">
            Instructor coaching
          </p>
          <div className="flex items-baseline gap-3 mt-0.5 flex-wrap">
            <p
              data-testid="instructor-total"
              className="text-3xl font-extrabold text-white"
            >
              {instructor.total_out_of_51 ?? 0}
              <span className="text-base text-slate-400">/51</span>
            </p>
            <p
              data-testid="instructor-pct"
              className="text-base font-extrabold text-slate-300"
            >
              {instructor.pct ?? 0}%
            </p>
            <span
              data-testid="instructor-band"
              className={`text-[10px] uppercase tracking-widest font-extrabold px-2 py-0.5 rounded-full border ${BAND_TONE[band] || ""}`}
            >
              {BAND_LABELS[band] || band}
            </span>
          </div>
          {instructor.key_reason && (
            <p
              data-testid="instructor-key-reason"
              className="text-[12px] text-slate-300 mt-1 leading-snug"
            >
              {instructor.key_reason}
            </p>
          )}
        </div>
      </div>
      <details className="mt-3">
        <summary
          data-testid="instructor-competency-toggle"
          className="cursor-pointer text-[10px] uppercase tracking-widest font-extrabold text-violet-300 hover:text-violet-200"
        >
          17-competency examiner sheet
        </summary>
        <div className="mt-2 space-y-3">
          {Object.entries(grouped).map(([cat, rows]) => (
            <div key={cat}>
              <p className="text-[9px] uppercase tracking-widest text-slate-500 font-extrabold mb-1">
                {cat}
              </p>
              <ul className="space-y-1">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`competency-${r.id}`}
                    className="flex items-start gap-2 text-[11px]"
                  >
                    <span
                      className={`shrink-0 w-6 h-6 rounded text-center leading-6 font-extrabold ${
                        r.score_0_3 >= 2
                          ? "bg-emerald-500/20 text-emerald-300"
                          : r.score_0_3 === 1
                          ? "bg-amber-500/20 text-amber-300"
                          : "bg-rose-500/20 text-rose-300"
                      }`}
                    >
                      {r.score_0_3}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 leading-snug">{r.name}</p>
                      {r.note && (
                        <p className="text-slate-500 text-[10px] mt-0.5">
                          {r.note}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function EvidenceLedger({ evidence, onSeek }) {
  if (!evidence?.length) return null;
  return (
    <div
      data-testid="dual-score-evidence"
      className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-4"
    >
      <p className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400 mb-2 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" />
        Timestamped evidence ({evidence.length})
      </p>
      <ul className="space-y-2">
        {evidence.map((e, i) => (
          <li
            key={i}
            data-testid={`evidence-row-${i}`}
            className="flex items-start gap-2 text-[12px] leading-snug"
          >
            <button
              type="button"
              data-testid={`evidence-seek-${i}`}
              onClick={() => onSeek?.(e.timestamp_seconds)}
              className="shrink-0 text-[10px] font-extrabold bg-slate-800 hover:bg-slate-700 text-indigo-300 px-1.5 py-0.5 rounded font-mono"
              title="Tap to jump to this moment in the video"
            >
              {fmtTs(e.timestamp_seconds)}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-slate-200">{e.what}</p>
              {e.why && (
                <p className="text-slate-500 text-[11px] mt-0.5">{e.why}</p>
              )}
              <div className="flex flex-wrap gap-1 mt-1">
                <span
                  className={`text-[9px] uppercase tracking-widest font-extrabold px-1.5 py-0.5 rounded-full border ${FAULT_TONE[e.fault] || ""}`}
                >
                  Fault: {e.fault}
                </span>
                {e.score_area && (
                  <span className="text-[9px] uppercase tracking-widest font-extrabold px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                    {e.score_area.replace(/_/g, " ")}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionPlan({ plan }) {
  if (!plan) return null;
  const hasFocus = (plan.next_lesson_focus || []).length > 0;
  const hasDrills = (plan.specific_drills || []).length > 0;
  if (!hasFocus && !hasDrills) return null;
  return (
    <div
      data-testid="dual-score-action-plan"
      className="rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-4"
    >
      <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-300 mb-2 flex items-center gap-1.5">
        <Target className="w-3.5 h-3.5" />
        Next lesson action plan
      </p>
      {hasFocus && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-extrabold mb-1">
            Focus
          </p>
          <ul className="space-y-1 mb-3">
            {plan.next_lesson_focus.map((f, i) => (
              <li
                key={i}
                className="text-[12px] text-slate-200 flex items-start gap-2"
              >
                <Sparkles className="w-3 h-3 text-emerald-300 mt-1 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasDrills && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-extrabold mb-1">
            Specific drills
          </p>
          <ul className="space-y-1">
            {plan.specific_drills.map((d, i) => (
              <li
                key={i}
                className="text-[12px] text-slate-200 flex items-start gap-2"
              >
                <Target className="w-3 h-3 text-emerald-300 mt-1 shrink-0" />
                {d}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function DualScorePanel({ dualScore, onSeek, reviewIntent = null }) {
  if (!dualScore) return null;

  // Phase D trust loop (Feb 13, 2026) — resolve the canonical
  // backend verdict ONCE; pass to PupilCard for the headline pill.
  const verdict = resolveVerdict(dualScore, reviewIntent);

  // F2 visual honesty state — when the backend explicitly says
  // there's no driving evidence, suppress all pass/fail implication
  // colours and surface a calm slate banner with the reason. This
  // replaces the entire panel; no scores are rendered because none
  // are meaningful when no_evidence_captured=true.
  if (verdict.isNoEvidence) {
    return (
      <section
        data-testid="dual-score-no-evidence"
        className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-500/15 flex items-center justify-center border border-slate-500/30 shrink-0">
            <AlertTriangle className="w-5 h-5 text-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              data-testid="no-evidence-verdict"
              className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400"
            >
              {verdict.label}
            </p>
            <p className="text-base font-extrabold text-white mt-0.5 leading-tight">
              No verdict for this recording
            </p>
            <p
              data-testid="no-evidence-reason"
              className="text-[13px] text-slate-300 mt-1.5 leading-relaxed"
            >
              {verdict.noEvidenceReason}
            </p>
            <p className="text-[11px] text-slate-500 mt-2">
              Re-upload with audio that captures the drive — the AI
              needs to hear what was happening to score it fairly.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // If the extractor errored, render a quiet info card so the user
  // knows scoring is unavailable for this review (instead of
  // showing 0% everywhere and silently lying).
  if (dualScore.error && !dualScore.pupil_score && !dualScore.instructor_score) {
    return (
      <div
        data-testid="dual-score-unavailable"
        className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-4"
      >
        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
          Dual scoring was unavailable for this review. Strengths,
          weaknesses, and the action plan below are still valid.
        </p>
      </div>
    );
  }
  return (
    <section
      data-testid="dual-score-panel"
      data-display-verdict={verdict.verdict || undefined}
      data-is-legacy={verdict.isLegacy ? "1" : undefined}
      className="grid gap-3 sm:grid-cols-2"
    >
      <PupilCard pupil={dualScore.pupil_score} verdict={verdict} />
      <InstructorCard instructor={dualScore.instructor_score} />
      <div className="sm:col-span-2">
        <EvidenceLedger
          evidence={dualScore.evidence}
          onSeek={onSeek}
        />
      </div>
      <div className="sm:col-span-2">
        <ActionPlan plan={dualScore.action_plan} />
      </div>
    </section>
  );
}
